// AudioInputManager.js - Simple audio input monitoring (based on audioInputTest.html)

export class AudioInputManager {
    constructor() {
        this.context = null;
        this.mediaStream = null;
        this.mediaStreamSource = null;
        this.analyser = null;
        this.inputLevel = 0;
        this.levelUpdateInterval = null;
        this.onLevelUpdate = null;

        // Recording functionality
        this.recorder = null;
        this.isRecording = false;
        this.audioChunks = [];
    }

    // Initialize audio input (based on audioInputTest.html)
    async initialize() {
        try {
            console.log('🎙️ Initializing audio input...');

            // Create audio context
            this.context = new (window.AudioContext || window.webkitAudioContext)();
            console.log(`🎙️ Audio context created, state: ${this.context.state}`);

            // CRITICAL: Always resume context after user interaction (required by browsers)
            if (this.context.state !== 'running') {
                console.log('🎙️ Audio context not running, attempting to resume...');
                await this.context.resume();
                console.log(`🎙️ Audio context resumed, new state: ${this.context.state}`);

                // Wait a bit for context to fully activate
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Request access to the microphone (same as test page)
            console.log('🎙️ Requesting microphone access...');
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                }
            });

            console.log('🎙️ Microphone access granted');
            console.log(`🎙️ Media stream tracks: ${this.mediaStream.getTracks().length}`);

            // Log track details
            this.mediaStream.getTracks().forEach((track, index) => {
                console.log(`🎙️ Track ${index}: ${track.kind}, enabled: ${track.enabled}, readyState: ${track.readyState}`);
            });

            // Create media stream source
            this.mediaStreamSource = this.context.createMediaStreamSource(this.mediaStream);
            console.log('🎙️ Media stream source created');

            // Create analyser for level metering
            this.analyser = this.context.createAnalyser();
            this.analyser.fftSize = 2048;
            console.log(`🎙️ Analyser created, fftSize: ${this.analyser.fftSize}`);

            this.mediaStreamSource.connect(this.analyser);
            console.log('🎙️ Media stream connected to analyser');

            // Wait a moment for the connection to stabilize
            await new Promise(resolve => setTimeout(resolve, 200));

            // Test if we're getting audio data
            const testBuffer = new Float32Array(this.analyser.frequencyBinCount);
            this.analyser.getFloatTimeDomainData(testBuffer);
            const hasNonZeroData = testBuffer.some(sample => Math.abs(sample) > 0.00001);
            console.log(`🎙️ Initial audio test - Has non-zero data: ${hasNonZeroData}`);
            if (!hasNonZeroData) {
                console.warn('🎙️ WARNING: No audio data detected. Microphone might be muted or not working.');
            }

            // Start level metering
            this.startLevelMetering();

            console.log('🎙️ Audio input initialized successfully');
            return true;
        } catch (error) {
            console.error('🎙️ Error initializing audio input:', error);
            console.error('🎙️ Error details:', error.name, error.message);
            return false;
        }
    }

    // Start level metering (exactly like audioInputTest.html)
    startLevelMetering() {
        if (!this.analyser) {
            console.error('🎙️ No analyser available for level metering');
            return;
        }

        console.log('🎙️ Starting level metering...');
        const bufferLength = this.analyser.frequencyBinCount;
        const dataArray = new Float32Array(bufferLength);
        console.log(`🎙️ Buffer length: ${bufferLength}`);

        // Update level every 100ms (same as test page)
        this.levelUpdateInterval = setInterval(() => {
            this.analyser.getFloatTimeDomainData(dataArray);

            // Calculate RMS (Root Mean Square) - same as test page
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                sum += dataArray[i] * dataArray[i];
            }
            const rms = Math.sqrt(sum / bufferLength);

            // Convert to dB (avoid log of 0) - same as test page
            let db = 20 * Math.log10(Math.max(rms, 0.00001));

            // Normalize to 0-1 range for UI - same as test page
            // -60dB is approximately silence, 0dB is maximum
            this.inputLevel = Math.max(0, Math.min(1, (db + 60) / 60));

            // Debug logging every 2 seconds
            if (Math.random() < 0.05) { // Log approximately every 2 seconds
                console.log(`🎙️ Audio Debug - RMS: ${rms.toFixed(6)}, dB: ${db.toFixed(1)}, Level: ${this.inputLevel.toFixed(3)}`);
                console.log(`🎙️ First 10 samples:`, Array.from(dataArray.slice(0, 10)).map(v => v.toFixed(4)));
            }

            // Call level update callback if provided
            if (this.onLevelUpdate) {
                this.onLevelUpdate(this.inputLevel);
            }
        }, 100);
    }

    // Set level update callback
    setLevelUpdateCallback(callback) {
        this.onLevelUpdate = callback;
    }

    // Start recording (same as audioInputTest.html)
    startRecording() {
        if (!this.mediaStream || this.isRecording) {
            console.warn('🎙️ Cannot start recording: no media stream or already recording');
            return false;
        }

        console.log('🎙️ Starting recording...');
        this.audioChunks = [];
        this.isRecording = true;

        // Create MediaRecorder (same as test page)
        this.recorder = new MediaRecorder(this.mediaStream, {
            mimeType: 'audio/webm'
        });

        // Handle data available event
        this.recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this.audioChunks.push(event.data);
            }
        };

        // Start recording
        this.recorder.start(100); // Collect data every 100ms
        console.log('🎙️ Recording started');
        return true;
    }

    // Stop recording and return AudioBuffer (same as audioInputTest.html)
    async stopRecording() {
        if (!this.recorder || !this.isRecording) {
            console.warn('🎙️ Cannot stop recording: no active recording');
            return null;
        }

        console.log('🎙️ Stopping recording...');

        return new Promise((resolve) => {
            this.recorder.onstop = async () => {
                try {
                    // Create blob from chunks
                    const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
                    console.log(`🎙️ Recording blob created: ${audioBlob.size} bytes`);

                    // Convert blob to AudioBuffer
                    const audioBuffer = await this.blobToAudioBuffer(audioBlob);
                    console.log(`🎙️ Recording converted to AudioBuffer: ${audioBuffer.duration.toFixed(2)}s`);

                    this.isRecording = false;
                    resolve(audioBuffer);
                } catch (error) {
                    console.error('🎙️ Error processing recording:', error);
                    this.isRecording = false;
                    resolve(null);
                }
            };

            this.recorder.stop();
        });
    }

    // Convert blob to AudioBuffer (same as audioInputTest.html)
    async blobToAudioBuffer(blob) {
        const arrayBuffer = await blob.arrayBuffer();
        return new Promise((resolve, reject) => {
            this.context.decodeAudioData(arrayBuffer)
                .then(resolve)
                .catch(reject);
        });
    }

    // Clean up resources (same as test page)
    dispose() {
        console.log('🎙️ Disposing audio input manager');

        // Stop recording if active
        if (this.recorder && this.isRecording) {
            this.recorder.stop();
            this.isRecording = false;
        }

        // Stop level metering
        if (this.levelUpdateInterval) {
            clearInterval(this.levelUpdateInterval);
            this.levelUpdateInterval = null;
        }

        // Disconnect media stream source
        if (this.mediaStreamSource) {
            this.mediaStreamSource.disconnect();
            this.mediaStreamSource = null;
        }

        // Stop media stream tracks
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        this.analyser = null;
        this.context = null;
        this.onLevelUpdate = null;
        this.inputLevel = 0;
        this.recorder = null;
        this.audioChunks = [];
    }
}